import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const toB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});
const mask = (a) => (a ? `******${String(a).slice(-4)}` : "—");

function downloadReport(results) {
  const head = ["TMF Vendor ID", "Vendor", "Phone", "LGA", "State", "Shop address", "Account", "Result", "Monnify account name", "Approval", "USSD access"];
  const lines = results.map((r) => [r.vendor_id || "", r.owner, r.phone, r.lga, r.state, r.address, r.account_masked,
    r.created ? "Created (pending)" : (r.message || "Not created"), r.monnify_account_name || r.lookup_error || "",
    r.created ? "Pending" : "", r.created ? "After approval" : ""]);
  const csv = [head, ...lines].map((l) => l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `tmf-vendor-upload-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}

function PreviewTable({ rows, sel, setSel }) {
  const toggle = (i) => setSel((s) => ({ ...s, [i]: !s[i] }));
  return (
    <div className="mt-3 overflow-auto max-h-[420px] rounded-xl border border-emerald-100">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-emerald-50"><tr className="text-left text-gray-500 uppercase">
          <th className="p-2"></th><th>#</th><th>Vendor / shop</th><th>Phone</th><th>LGA / State</th><th>Account</th><th>Monnify name</th><th>Check</th></tr></thead>
        <tbody data-testid="bulk-preview-rows">
          {rows.map((r, i) => (
            <tr key={r.row} className="border-t border-gray-100 align-top" data-testid={`bulk-row-${r.row}`}>
              <td className="p-2"><input type="checkbox" data-testid={`bulk-select-${r.row}`} disabled={r.errors.length > 0} checked={!!sel[i]} onChange={() => toggle(i)} /></td>
              <td className="py-2">{r.row}</td>
              <td className="py-2"><b>{r.owner}</b><div className="text-gray-500">{r.business_name}</div></td>
              <td className="py-2">{r.phone}{r.alt_phone && <div className="text-gray-400">{r.alt_phone}</div>}</td>
              <td className="py-2">{r.lga}<div className="text-gray-500">{r.state}</div></td>
              <td className="py-2 font-mono">{mask(r.moniepoint_account_number)}</td>
              <td className="py-2">{r.monnify_account_name || "—"}</td>
              <td className="py-2 max-w-[260px]">
                {r.clean && <Badge className="bg-emerald-100 text-emerald-800">Clean</Badge>}
                {r.errors.map((e) => <div key={e} className="text-red-600">{e}</div>)}
                {r.warnings.map((w) => <div key={w} className="text-amber-700">{w}</div>)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BulkVendorUpload({ onSaved }) {
  const [prev, setPrev] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setResults(null);
    try {
      const { data } = await api.post("/admin/vendors/bulk/preview", { filename: file.name, content_b64: await toB64(file) });
      setPrev(data);
      setSel(Object.fromEntries(data.rows.map((r, i) => [i, r.clean])));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const register = async () => {
    const rows = prev.rows.filter((_, i) => sel[i]).map((r) => ({
      owner: r.owner, business_name: r.business_name, phone: r.phone, alt_phone: r.alt_phone || null, address: r.address,
      lga: r.lga, state: r.state, moniepoint_account_number: r.moniepoint_account_number, account_name: r.account_name }));
    setBusy(true);
    try {
      const { data } = await api.post("/admin/vendors/bulk/register", { rows });
      setResults(data.results); setPrev(null); onSaved();
      toast.success(`${data.results.filter((r) => r.created).length} of ${rows.length} vendors registered (pending)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const count = Object.values(sel).filter(Boolean).length;
  return (
    <div className="mt-3 rounded-xl border border-dashed border-emerald-200 p-3" data-testid="bulk-vendor-upload">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-emerald-300 px-3 py-1.5 text-sm text-emerald-800 hover:bg-emerald-50" data-testid="bulk-upload-label">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Bulk upload vendors (.xlsx, .csv, .docx)
          <input type="file" accept=".xlsx,.csv,.docx" className="hidden" onChange={onFile} disabled={busy} data-testid="bulk-upload-input" />
        </label>
        <span className="text-xs text-gray-500">Rows are checked with Monnify before anything is saved. Only clean rows are pre-selected.</span>
      </div>
      {prev && (
        <>
          <div className="mt-3 text-sm" data-testid="bulk-preview-summary"><b>{prev.total}</b> rows · <span className="text-emerald-700">{prev.clean} clean</span> · <span className="text-red-600">{prev.errors} with errors</span></div>
          <PreviewTable rows={prev.rows} sel={sel} setSel={setSel} />
          <div className="mt-3 flex gap-2">
            <Button size="sm" disabled={busy || !count} onClick={register} data-testid="bulk-register-btn" className="bg-emerald-600 hover:bg-emerald-700">Register selected ({count})</Button>
            <Button size="sm" variant="ghost" onClick={() => setPrev(null)} data-testid="bulk-cancel-btn">Cancel</Button>
          </div>
        </>
      )}
      {results && (
        <div className="mt-3 text-sm" data-testid="bulk-results">
          {results.filter((r) => r.created).length} created, {results.filter((r) => !r.created).length} skipped.
          <Button size="sm" variant="outline" className="ml-3" onClick={() => downloadReport(results)} data-testid="bulk-report-btn"><Download className="w-4 h-4 mr-1" /> Download report</Button>
        </div>
      )}
    </div>
  );
}
